import { Box, Button, Link, TextField, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import AuthLayout from "../../components/commonComponents/authLayout/authLayout";
import InlineAlert from "../../components/reusableComponents/inlineAlert/inlineAlert";
import { tokens, mono } from "../../theme";
import {
  reset,
  setLoggedIn,
  logout,
} from "../../store/features/authentication/authenticationSlice";
import {
  generateTwoFactor,
  verifyTwoFactor,
} from "../../store/thunks/authenticationThunk";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

/** d••••@midwestind.com — enough to confirm the inbox without exposing it. */
const maskEmail = (email?: string) => {
  if (!email) return "your email";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 1)}${"•".repeat(Math.max(local.length - 1, 1))}@${domain}`;
};

const boxSx = {
  width: { xs: 44, sm: 52 },
  "& .MuiOutlinedInput-root": { height: 56 },
  "& input": {
    textAlign: "center",
    fontFamily: mono,
    fontSize: 22,
    padding: 0,
  },
};

const TwoFactor = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.authentication);

  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [errorMessage, setErrorMessage] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const code = digits.join("");

  const sendCode = useCallback(() => {
    dispatch(reset());
    if (!user?.user_name) {
      dispatch(logout());
      navigate("/login");
      return;
    }
    setErrorMessage("");
    setSecondsLeft(RESEND_SECONDS);
    dispatch(
      generateTwoFactor({
        email: user.user_name,
        name: `${user.first_name}, ${user.last_name}`,
      })
    ).then((result: any) => {
      if (!result?.payload?.result) {
        setErrorMessage(result?.payload?.message ?? "We couldn't send a code. Try again.");
      }
    });
  }, [dispatch, navigate, user]);

  // Sent on arrival: the old screen made you press "Email OTP" first, which
  // read as an extra step rather than the point of the page.
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    sendCode();
  }, [sendCode]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const verify = useCallback(
    (value: string) => {
      setVerifying(true);
      dispatch(verifyTwoFactor({ otp: value, user_index: user?.client_user_index }))
        .then((result: any) => {
          if (result?.payload?.result) {
            dispatch(setLoggedIn(true));
            navigate("/dashboard");
          } else {
            setDigits(Array(CODE_LENGTH).fill(""));
            setErrorMessage(result?.payload?.message ?? "That code isn't right.");
            inputs.current[0]?.focus();
          }
        })
        .finally(() => setVerifying(false));
    },
    [dispatch, navigate, user]
  );

  const setDigit = (index: number, raw: string) => {
    const chars = raw.replace(/\D/g, "").split("");
    if (chars.length === 0) {
      setDigits((prev) => prev.map((d, i) => (i === index ? "" : d)));
      return;
    }
    // Pasting the whole code into any box fills the row.
    setDigits((prev) => {
      const next = [...prev];
      chars.slice(0, CODE_LENGTH - index).forEach((c, k) => {
        next[index + k] = c;
      });
      const filled = Math.min(index + chars.length, CODE_LENGTH - 1);
      inputs.current[filled]?.focus();
      return next;
    });
    setErrorMessage("");
  };

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  return (
    <AuthLayout>
      <Link
        component="button"
        type="button"
        onClick={() => {
          dispatch(logout());
          navigate("/login");
        }}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 13, alignSelf: "flex-start" }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
        Back to log in
      </Link>

      <Box>
        <Typography variant="h2" sx={{ mb: 0.5 }}>
          Check your email
        </Typography>
        <Typography sx={{ fontSize: tokens.typography.body, color: tokens.color.muted }}>
          We sent a six-digit code to <strong>{maskEmail(user?.user_name)}</strong>. It expires in
          10 minutes.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: { xs: 0.75, sm: 1 }, justifyContent: "space-between" }}>
        {digits.map((digit, i) => (
          <TextField
            key={i}
            value={digit}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={handleKeyDown(i)}
            inputRef={(el) => (inputs.current[i] = el)}
            error={Boolean(errorMessage)}
            sx={boxSx}
            inputProps={{
              inputMode: "numeric",
              autoComplete: i === 0 ? "one-time-code" : "off",
              "aria-label": `Digit ${i + 1} of ${CODE_LENGTH}`,
              maxLength: CODE_LENGTH,
            }}
          />
        ))}
      </Box>

      {errorMessage && <InlineAlert>{errorMessage}</InlineAlert>}

      <Button
        variant="contained"
        fullWidth
        disabled={code.length !== CODE_LENGTH || verifying}
        onClick={() => verify(code)}
        sx={{ height: { xs: 48, md: 46 } }}
      >
        Verify code
      </Button>

      <Typography sx={{ fontSize: 13, color: tokens.color.muted, textAlign: "center" }}>
        Didn't get it?{" "}
        {secondsLeft > 0 ? (
          <Box component="span" sx={{ fontFamily: mono }}>
            Resend in 0:{String(secondsLeft).padStart(2, "0")}
          </Box>
        ) : (
          <Link component="button" type="button" onClick={sendCode} sx={{ fontSize: 13 }}>
            Resend code
          </Link>
        )}
      </Typography>
    </AuthLayout>
  );
};

export default TwoFactor;
